<template>
  <div
    class="inga-portrait-wrap"
    :class="{
      'is-reward': frame === 'reward',
      'is-zoomable': frame === 'reward' && lightbox,
      'is-fade': hasMany
    }"
    :role="frame === 'reward' && lightbox ? 'button' : undefined"
    :tabindex="frame === 'reward' && lightbox ? 0 : undefined"
    :style="{ '--reward-fade': fadeDuration }"
    @click="openLightbox"
    @keydown.enter="openLightbox"
  >
    <img
      v-if="!hasMany"
      class="img-fluid cartoon-img"
      :src="src"
      :alt="alt"
    />
    <img
      v-for="(url, i) in gallery"
      v-else
      :key="url + '-' + i"
      class="img-fluid cartoon-img fade-layer"
      :class="{ 'is-on': i === index }"
      :src="url"
      :alt="alt"
    />
    <button
      v-if="showNav"
      type="button"
      class="portrait-nav is-prev"
      aria-label="Předchozí obrázek"
      @click.stop="step(-1)"
    >‹</button>
    <button
      v-if="showNav"
      type="button"
      class="portrait-nav is-next"
      aria-label="Další obrázek"
      @click.stop="step(1)"
    >›</button>
    <div v-if="showNav" class="portrait-count">{{ index + 1 }} / {{ gallery.length }}</div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useStore } from "vuex";

const props = defineProps({
  src: { type: String, required: true },
  alt: { type: String, default: "INGA" },
  frame: { type: String, default: "auto" },
  sources: { type: Array, default: () => [] },
  rotate: { type: Boolean, default: false },
  nav: { type: Boolean, default: false },
  rotateMs: { type: Number, default: 4800 },
  fadeMs: { type: Number, default: 1150 },
  rotateDelay: { type: Number, default: 0 },
  lightbox: { type: Boolean, default: true }
});

const store = useStore();
const index = ref(0);
let timer = null;
let startTimer = null;

const gallery = computed(() => {
  const fromSources = Array.isArray(props.sources)
    ? props.sources.map((item) => String(item || "").trim()).filter(Boolean)
    : [];
  const unique = [];
  fromSources.forEach((item) => {
    if (!unique.includes(item)) unique.push(item);
  });
  if (unique.length) return unique;
  return props.src ? [props.src] : [];
});

const hasMany = computed(() => props.frame === "reward" && gallery.value.length > 1);
const canRotate = computed(() => props.rotate && hasMany.value);
const showNav = computed(() => props.nav && hasMany.value);
const fadeDuration = computed(() => `${Math.max(400, Number(props.fadeMs) || 1150)}ms`);

const clearTimers = () => {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
  if (startTimer) {
    clearTimeout(startTimer);
    startTimer = null;
  }
};

const step = (delta) => {
  const len = gallery.value.length;
  if (len < 2) return;
  index.value = (index.value + delta + len) % len;
};

const tick = () => {
  if (document.hidden || !canRotate.value) return;
  step(1);
};

const startRotate = () => {
  clearTimers();
  index.value = 0;
  if (!canRotate.value) return;
  const period = Math.max(2200, Number(props.rotateMs) || 4800);
  const delay = Math.max(0, Number(props.rotateDelay) || 0);
  startTimer = setTimeout(() => {
    tick();
    timer = setInterval(tick, period);
  }, period + delay);
};

watch(() => gallery.value.join("|"), () => {
  startRotate();
});

onMounted(startRotate);
onUnmounted(clearTimers);

const openLightbox = (event) => {
  if (!props.lightbox || props.frame !== "reward") return;
  const sources = gallery.value.length ? gallery.value : (props.src ? [props.src] : []);
  if (!sources.length) return;
  event?.stopPropagation?.();
  event?.preventDefault?.();
  store.dispatch("openLightbox", {
    src: sources[index.value] || sources[0],
    sources,
    index: index.value,
    alt: props.alt || "Výhra"
  });
};
</script>

<style scoped>
.inga-portrait-wrap {
  width: 100%;
  overflow: hidden;
  border-radius: 0.65rem;
}

.inga-portrait-wrap.is-reward {
  position: relative;
  aspect-ratio: 455 / 355;
  background: #e9ecef;
}

.inga-portrait-wrap.is-zoomable {
  cursor: zoom-in;
}

.inga-portrait-wrap.is-reward .cartoon-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.inga-portrait-wrap.is-fade .fade-layer {
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity var(--reward-fade, 1.15s) ease-in-out;
}

.inga-portrait-wrap.is-fade .fade-layer.is-on {
  opacity: 1;
}

.portrait-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  width: 2.1rem;
  height: 2.1rem;
  border: 0;
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.62);
  color: #fff;
  font-size: 1.45rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0 0.1rem;
}

.portrait-nav:hover {
  background: rgba(255, 138, 76, 0.92);
}

.portrait-nav.is-prev {
  left: 0.55rem;
}

.portrait-nav.is-next {
  right: 0.55rem;
}

.portrait-count {
  position: absolute;
  z-index: 2;
  bottom: 0.45rem;
  left: 50%;
  transform: translateX(-50%);
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  background: rgba(15, 23, 42, 0.62);
  padding: 0.12rem 0.5rem;
  border-radius: 999px;
  pointer-events: none;
}

.cartoon-img {
  width: 100%;
  height: auto;
  object-fit: contain;
  object-position: center;
  display: block;
  border: 0;
  outline: none;
  box-shadow: none;
  background: transparent;
  border-radius: 0.65rem;
  pointer-events: none;
}
</style>
